/**
 * Remove the retired theme service worker without touching other registrations.
 *
 * The old worker was registered from the theme directory and cached dynamic
 * WordPress/WooCommerce responses. Keep this cleanup in the core bundle for at
 * least one production release after the worker files are removed.
 */

const CLEANUP_KEY = 'simpaticoLegacyServiceWorkerRemovedV1';
const LEGACY_WORKER_PATH = '/wp-content/themes/simpaticobike/service-worker.js';
const LEGACY_CACHE_PREFIX = 'simpatico-';

/**
 * Return the script URL for any lifecycle state in a registration.
 *
 * @param {ServiceWorkerRegistration} registration Service worker registration.
 * @return {string} Script URL or an empty string.
 */
function getRegistrationScriptUrl(registration) {
  const worker = registration.active || registration.waiting || registration.installing;
  return worker && worker.scriptURL ? worker.scriptURL : '';
}

/**
 * Check whether a registration belongs to the retired theme worker.
 *
 * @param {ServiceWorkerRegistration} registration Service worker registration.
 * @param {string} origin Current site origin.
 * @return {boolean} Whether the registration should be removed.
 */
function isLegacyRegistration(registration, origin) {
  const scriptUrl = getRegistrationScriptUrl(registration);
  if (!scriptUrl) {
    return false;
  }

  try {
    const url = new URL(scriptUrl, origin);
    return url.origin === origin && url.pathname.endsWith(LEGACY_WORKER_PATH);
  } catch (error) {
    return false;
  }
}

/**
 * Remove only Simpatico theme worker state from the current browser.
 *
 * @param {Window} environment Browser environment.
 * @return {Promise<void>} Resolves after registrations and caches are handled.
 */
async function cleanupLegacyServiceWorker(environment) {
  let storage = null;

  try {
    storage = environment.localStorage;
  } catch (error) {
    // Storage can be disabled; cleanup should still proceed.
    storage = null;
  }

  const tasks = [];
  const serviceWorker = environment.navigator && environment.navigator.serviceWorker;

  if (serviceWorker && typeof serviceWorker.getRegistrations === 'function') {
    tasks.push(
      serviceWorker.getRegistrations().then((registrations) => Promise.all(
        registrations
          .filter((registration) => isLegacyRegistration(registration, environment.location.origin))
          .map((registration) => registration.unregister())
      ))
    );
  }

  if (environment.caches && typeof environment.caches.keys === 'function') {
    tasks.push(
      environment.caches.keys().then((cacheNames) => Promise.all(
        cacheNames
          .filter((cacheName) => cacheName.startsWith(LEGACY_CACHE_PREFIX))
          .map((cacheName) => environment.caches.delete(cacheName))
      ))
    );
  }

  await Promise.all(tasks);

  if (storage) {
    try {
      storage.setItem(CLEANUP_KEY, '1');
    } catch (error) {
      // A storage quota/privacy failure must not affect the storefront.
    }
  }
}

if (typeof window !== 'undefined') {
  cleanupLegacyServiceWorker(window).catch(() => {
    // Cleanup is best-effort and must never affect the storefront.
  });
}
